import { useEffect, useRef, useState } from "react";
import MockupConfigAPI from "../../../api/MockupConfigAPI";
import type { PSDTemplate } from "../../../models/MockupConfig";
import { getMockupEditorPreviewUrl } from "./getMockupEditorPreviewUrl";

type UseMockupEditorPreviewUrlOptions = {
  template: PSDTemplate | null | undefined;
  logoUrl: string | null;
  onError?: (title: string, message?: string) => void;
  onBlankPreviewResolved?: (templateId: number, url: string) => void;
};

export function useMockupEditorPreviewUrl({
  template,
  logoUrl,
  onError,
  onBlankPreviewResolved,
}: UseMockupEditorPreviewUrlOptions) {
  const [resolvedBlankUrl, setResolvedBlankUrl] = useState<string | null>(null);
  const [loadingPlacementPreview, setLoadingPlacementPreview] = useState(false);
  const apiRef = useRef(new MockupConfigAPI());

  useEffect(() => {
    if (!logoUrl) {
      setResolvedBlankUrl(null);
      setLoadingPlacementPreview(false);
      return;
    }

    const templateId = template?.id;
    if (!templateId) {
      setLoadingPlacementPreview(false);
      return;
    }

    if (template.blank_preview_url) {
      setResolvedBlankUrl(template.blank_preview_url);
      setLoadingPlacementPreview(false);
      return;
    }

    let cancelled = false;
    setLoadingPlacementPreview(true);

    apiRef.current
      .ensureBlankPreviewUrl(templateId)
      .then((url) => {
        if (cancelled) return;
        setResolvedBlankUrl(url);
        onBlankPreviewResolved?.(templateId, url);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        onError?.(
          "Blank preview failed",
          (error as Error)?.message || "Could not load placement preview",
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingPlacementPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    logoUrl,
    template?.id,
    template?.blank_preview_url,
    onError,
    onBlankPreviewResolved,
  ]);

  const previewImageUrl = getMockupEditorPreviewUrl(
    template,
    Boolean(logoUrl),
    resolvedBlankUrl,
  );

  return { previewImageUrl, loadingPlacementPreview };
}
